import { Inject, Injectable } from '@angular/core'

import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Observable, of } from 'rxjs'
import { catchError, switchMap, withLatestFrom } from 'rxjs/operators'

import { AggregationAnalysisApiService } from '~/core/api'
import { IAggregationAnalysisQuarter, IAggregationAnalysisSector } from '~/core/models'

import * as AGGREGATION_ANALYSIS_ACTIONS from './aggregation-analysis.actions'
import { AGGREGATION_ANALYSIS_MINIMUM_COMPANY_COUNT } from './aggregation-analysis.constant'
import { AGGREGATION_ANALYSIS_FACADE, IAggregationAnalysisFacade } from './aggregation-analysis.facade'

@Injectable()
export class AggregationAnalysisEffects {
  constructor(
    private actions$: Actions,
    private aggregationAnalysisApiService: AggregationAnalysisApiService,
    @Inject(AGGREGATION_ANALYSIS_FACADE) private aggregationAnalysisFacade: IAggregationAnalysisFacade
  ) {}

  // quarter
  getAggregationAnalysisQuarterData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AGGREGATION_ANALYSIS_ACTIONS.getAggregationAnalysisQuarterData),
      withLatestFrom(this.aggregationAnalysisFacade.aggType$, this.aggregationAnalysisFacade.quarter$),
      switchMap(([, aggType, quarter]) =>
        // Don't reload data if exist for the same quarter data
        (quarter.initialized && aggType === quarter.aggType
          ? of(quarter.data)
          : this.aggregationAnalysisApiService.getAggregationAnalysisQuarterList(aggType)
        ).pipe(
          switchMap(data => [
            AGGREGATION_ANALYSIS_ACTIONS.setAggregationAnalysisQuarterSuccess({ data, aggType }),
            AGGREGATION_ANALYSIS_ACTIONS.setAggregationAnalysisQuarterSelected({
              selected: this.findSelectedQuarter(data, quarter.selected),
            }),
          ])
        )
      )
    )
  )

  setAggregationAnalysisQuarterSelected$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AGGREGATION_ANALYSIS_ACTIONS.setAggregationAnalysisQuarterSelected),
      switchMap(() => [AGGREGATION_ANALYSIS_ACTIONS.getAggregationAnalysisSectorData()])
    )
  )

  // sector
  getAggregationAnalysisSectorData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AGGREGATION_ANALYSIS_ACTIONS.getAggregationAnalysisSectorData),
      withLatestFrom(
        this.aggregationAnalysisFacade.aggType$,
        this.aggregationAnalysisFacade.quarterSelected$,
        this.aggregationAnalysisFacade.sector$
      ),
      switchMap(([, aggType, quarterSelected, sector]) => {
        let sectorData$: Observable<IAggregationAnalysisSector[]>
        if (!quarterSelected && sector.data.length === 0) {
          sectorData$ = of(sector.data)
        } else if (!quarterSelected) {
          sectorData$ = of([])
        } else if (sector.initialized && sector.aggType === aggType && sector.quarter === quarterSelected) {
          // Don't reload data if exist for the same quarter
          sectorData$ = of(sector.data)
        } else {
          sectorData$ = this.aggregationAnalysisApiService.getAggregationAnalysisSectorList(
            aggType,
            quarterSelected.laterYear,
            quarterSelected.laterQuarter,
            sector.sortBy,
            sector.order
          )
        }

        return sectorData$.pipe(
          switchMap(data => [
            AGGREGATION_ANALYSIS_ACTIONS.setAggregationAnalysisSectorSuccess({
              data,
              aggType,
              quarter: quarterSelected,
            }),
          ]),
          catchError(error => [AGGREGATION_ANALYSIS_ACTIONS.setAggregationAnalysisSectorError({ error })])
        )
      })
    )
  )

  setAggregationAnalysisSectorSort$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AGGREGATION_ANALYSIS_ACTIONS.setAggregationAnalysisSectorSort),
      switchMap(() => [AGGREGATION_ANALYSIS_ACTIONS.getAggregationAnalysisSectorData()])
    )
  )

  // aggregation type
  setAggregationAnalysisType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AGGREGATION_ANALYSIS_ACTIONS.setAggregationAnalysisType),
      switchMap(() => [AGGREGATION_ANALYSIS_ACTIONS.getAggregationAnalysisQuarterData()])
    )
  )

  private findSelectedQuarter(
    data: IAggregationAnalysisQuarter[],
    previousSelected: IAggregationAnalysisQuarter | undefined
  ): IAggregationAnalysisQuarter | undefined {
    if (!data.length) {
      return undefined
    }
    if (previousSelected && data.includes(previousSelected)) {
      return previousSelected
    }
    if (previousSelected) {
      const newSelected = data.find(
        item => item.laterYear === previousSelected.laterYear && item.laterQuarter === previousSelected.laterQuarter
      )
      if (newSelected) {
        return newSelected
      }
    }
    for (let index = data.length - 1; index >= 0; index--) {
      const item = data[index]
      if (item.commonCount >= AGGREGATION_ANALYSIS_MINIMUM_COMPANY_COUNT) {
        return item
      }
    }
    return undefined
  }
}
